import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-dsgov';
import { useNavigate } from 'react-router-dom';
import CustomDivider from '../../../components/customDivider/customDivider';
import TituloSection from '../../../components/tituloSection/tituloSection';
import { declaracaoTitularArrendatarioInicial, DiefCfemContext } from '../../../contexts/DiefCfemContext';
import { useNotificacao } from '../../../hooks/useNotificacao';
import { ICrumb } from '../../../interfaces/ICrumb';
import { IDeclaracaoTitularOuArrendatario } from '../../../interfaces/IDeclaracoes';
import { IIdentificacaoBasica } from '../../../interfaces/IIdentificacaoBasica';
import TitularArrendatarioPassoInicial from '../components/titularArrendatarioPassoInicial/titularArrendatarioPassoInicial';
import TitularArrendatarioProcessoMinerario from '../components/titularArrendatarioProcessoMinerario/titularArrendatarioProcessoMinerario';
import TitularArrendatarioResumo from '../components/titularArrendatarioResumo/titularArrendatarioResumo';
import { useBackEndRequests } from '../lib/backEndRequests';
import titularArrendatarioEstadoProcesso, {
  IEstadoTitularArrendatario,
} from '../lib/titularArrendatarioEstadoProcesso';
import TitularArrendatarioDadosDeclaracao from '../components/titularArrendatarioDadosDeclaracao/titularArrendatarioDadosDeclaracao';
import BotoesAcao from './components/botoesAcao/botoesAcao';
import { ERegimesTributacao } from '../../../enums/ERegimesTributacao';
import CustomConfirmer from '../../../components/customConfirmer/customConfirmer';
import { mesParaAbreviacao } from 'utils/mesParaAbreviacao';
import AnmCheckboxDeclaracao from 'components/anmCheckboxDeclaracao/anmCheckboxDeclaracao';
import styles from './TitularArrendatarioEdit.module.scss';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { BrLoading } from '@govbr-ds/react-components';

function TitularArrendatarioEdit() {
  const ABA_CONCESSAO_LICENCIAMENTO_GUIA = 1;

  const urlParams = new URLSearchParams(window.location.search);
  const declaracaoId = urlParams.get('id') || '';

  const { getDeclaracaoById } = useBackEndRequests();

  const { data: declaracaoData, isLoading: isDeclaracaoDataLoading } = useQuery<
    IDeclaracaoTitularOuArrendatario,
    Error
  >({
    queryKey: ['declaracaoById', declaracaoId],
    queryFn: () => getDeclaracaoById(declaracaoId),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const cpfCnpj = sessionStorage.getItem('userCpfCnpj');
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const {
    postEnviarDeclaracao,
    getIdentificacaoBasica,
    putDeclaraProcessosOutrosRegimesSemOperacoes,
    putDeclaraDemaisProcessosSemOperacoes,
    checarCheckboxOutrosRegimes,
    checarCheckboxPLG,
  } = useBackEndRequests();

  const context = useContext(DiefCfemContext);
  const mostrarNotificacao = useNotificacao();

  const [isNovaOperacaoPLG, setIsNovaOperacaoPLG] = useState(false);
  const [isEdicaoOutrosRegimes, setIsEdicaoOutrosRegimes] = useState(false);
  const [isRetificadora, setIsRetificadora] = useState(false);

  const [isModalEnviarDeclaracaoOpen, setIsModalEnviarDeclaracaoOpen] = useState(false);

  const [estadoTela, setEstadoTela] = useState<IEstadoTitularArrendatario | null>(null);

  const [abaSelecionada, setAbaSelecionada] = useState(ABA_CONCESSAO_LICENCIAMENTO_GUIA);

  const [regimeTributacao, setRegimeTributacao] = useState<ERegimesTributacao | null>(null);
  const [hasDeclaracaoInicial, setHasDeclaracaoInicial] = useState<boolean>(true);

  const { data: declaranteData, isLoading: isDeclaranteDataLoading } = useQuery<
    IIdentificacaoBasica,
    IErrorWithSentryId
  >({
    queryKey: ['idBasicaQuery', cpfCnpj],
    queryFn: () => getIdentificacaoBasica(cpfCnpj || ''),
    refetchOnWindowFocus: false,
  });

  const [habilitarBotaoConfirmarEnvioPLG, setHabilitarBotaoConfirmarEnvioPLG] = useState(false);
  const [habilitarBotaoConfirmarEnvioOutrosRegimes, setHabilitarBotaoConfirmarEnvioOutrosRegimes] = useState(false);
  const [qtdeCheckboxesAApresentar, setQtdeCheckboxesAApresentar] = useState(0);
  const [isLoadingPLGCheckBox, setIsLoadingPLGCheckBox] = useState(false);
  const [isLoadingOutrosRegimesCheckBox, setIsLoadingOutrosRegimesCheckBox] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const periodoDeclaracao =
    mesParaAbreviacao(
      context?.declaracaoTitularArrendatario?.exercicioMes || declaracaoTitularArrendatarioInicial.exercicioMes
    ) +
    `/${context?.declaracaoTitularArrendatario?.exercicioAno || declaracaoTitularArrendatarioInicial.exercicioAno} `;
  const labelCheckboxOutrosRegimes = `Declaro que todos os processos desta seção estão sem movimentação no período de ${periodoDeclaracao}.`;
  const labelCheckboxPLG = `Declaro que os processos não selecionados nesta seção estão sem movimentação no período ${periodoDeclaracao}.`;

  useEffect(() => {
    if (estadoTela) {
      titularArrendatarioEstadoProcesso.salvarEstado(estadoTela);
    }
  }, [estadoTela]);

  useEffect(() => {
    const crumbs: ICrumb[] = [
      {
        isHome: true,
        label: 'home',
        onClick: () => {
          navigate('/');
        },
      },
      {
        label: 'Titular ou Arrendatário',
        onClick: () => {
          navigate('/titular-ou-arrendatario');
        },
      },
      {
        active: true,
        label: 'Criação da Declaração',
      },
    ];
    context?.setCrumbs(crumbs);
    // eslint-disable-next-line
  }, [navigate]);

  const enviarDeclaracao = async () => {
    const declaracao = context?.declaracaoTitularArrendatario;
    if (declaracao) {
      await postEnviarDeclaracao(declaracao.id)
        .then(async () => {
          window.scrollTo(0, 0);
          navigate(`/titular-ou-arrendatario/view?id=${declaracaoId}&status=enviado`);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao enviar declaração, tente novamente mais tarde.',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Declaração não encontrada',
      });
    }
    setIsModalEnviarDeclaracaoOpen(false);
  };

  useEffect(() => {
    if (declaracaoData) {
      setIsRetificadora(declaracaoData.tipoDeclaracao === 1);
    }
  }, [declaracaoData]);

  useEffect(() => {
    if (step === 2) {
      setQtdeCheckboxesAApresentar(0);
    }
  }, [step]);

  useEffect(() => {
    const declaracaoAntiga = context?.declaracaoTitularArrendatario || declaracaoTitularArrendatarioInicial;
    const declaracao: IDeclaracaoTitularOuArrendatario = {
      ...declaracaoAntiga,
      id: declaracaoId,
      statusDeclaracao: 0,
      tipoDeclarante: 0,
      tipoDeclaracao: 0,
      declarante: {
        nomeRazaoSocial: declaranteData?.nomeRazaoSocial || '',
        cpfCnpj: cpfCnpj || '',
      },
      exercicioMes: parseInt(sessionStorage.getItem('mesDeclaracao') || '-1'),
      exercicioAno: parseInt(sessionStorage.getItem('anoDeclaracao') || '-1'),
      operacoes: declaracaoAntiga.operacoes || [],
    };
    context?.setDeclaracaoTitularArrendatario(declaracao);
    // eslint-disable-next-line
  }, [declaracaoId, declaranteData, declaracaoData, isDeclaracaoDataLoading]);

  const getTextoComplementarEnvio = () => {
    if (step === 3 && qtdeCheckboxesAApresentar > 0) {
      let textoComplementar;
      textoComplementar = (
        <>
          <strong>Existem processos que não foram declarados.</strong>
          <br />
        </>
      );
      if (qtdeCheckboxesAApresentar === 1) {
        return (
          <>
            {textoComplementar}
            <span>
              Para prosseguir confirme a seguinte <strong>informação:</strong>
            </span>
          </>
        );
      }
      if (qtdeCheckboxesAApresentar > 1) {
        return (
          <>
            {textoComplementar}
            <span>
              Para prosseguir confirme as seguintes <strong>informações:</strong>
            </span>
          </>
        );
      }
    }
    return '';
  };

  return (
    <>
      <Modal title='Enviar Declaração de Titular ou Arrendatário' useScrim modalOpened={isModalEnviarDeclaracaoOpen}>
        <Modal.Body>
          {(isLoadingOutrosRegimesCheckBox || isLoadingPLGCheckBox) && (
            <div className={styles.tableLoading}>
              <BrLoading large label='Carregando...' />
            </div>
          )}
          {!isLoadingOutrosRegimesCheckBox && !isLoadingPLGCheckBox && getTextoComplementarEnvio() !== '' && (
            <div className={styles.textoComplementarEnvio}>{getTextoComplementarEnvio()}</div>
          )}
          <div
            className={
              styles.checkboxContainer +
              ' ' +
              (isLoadingOutrosRegimesCheckBox || isLoadingPLGCheckBox ? styles.checkboxContainerLoading : '')
            }
          >
            <AnmCheckboxDeclaracao
              title='Concessão/Licenciamento/Guia de Utilização'
              label={labelCheckboxOutrosRegimes}
              getFunctionQuery={() => checarCheckboxOutrosRegimes(declaracaoId)}
              putFunctionQuery={() => putDeclaraProcessosOutrosRegimesSemOperacoes(declaracaoId)}
              getQueryKey={['checkboxDeclaracaoOutrosRegimes', declaracaoId]}
              putQueryKey={['putCheckboxDeclaracaoOutrosRegimes', declaracaoId]}
              dispararGetQuery={step === 3 || isModalEnviarDeclaracaoOpen}
              id='checkbox-declaracao-outros-regimes'
              setHabilitarBotaoConfirmarEnvio={setHabilitarBotaoConfirmarEnvioOutrosRegimes}
              setQtddCheckboxesAAPresentar={setQtdeCheckboxesAApresentar}
              setIsLoadingCheckBox={setIsLoadingOutrosRegimesCheckBox}
            />
            <AnmCheckboxDeclaracao
              title='Permissão de Lavra Garimpeira'
              label={labelCheckboxPLG}
              getFunctionQuery={() => checarCheckboxPLG(declaracaoId)}
              putFunctionQuery={() => putDeclaraDemaisProcessosSemOperacoes(declaracaoId)}
              getQueryKey={['checkboxDeclaracaoPLG', declaracaoId]}
              putQueryKey={['putCheckboxDeclaracaoPLG', declaracaoId]}
              dispararGetQuery={step === 3 || isModalEnviarDeclaracaoOpen}
              id='checkbox-declaracao-plg'
              setHabilitarBotaoConfirmarEnvio={setHabilitarBotaoConfirmarEnvioPLG}
              setQtddCheckboxesAAPresentar={setQtdeCheckboxesAApresentar}
              setIsLoadingCheckBox={setIsLoadingPLGCheckBox}
            />
          </div>
          <div className={styles.mensagemConfirmacaoEnvioContainerWrapper}>
            <div
              className={
                styles.mensagemConfirmacaoEnvioContainer +
                ' ' +
                (isLoadingOutrosRegimesCheckBox || isLoadingPLGCheckBox
                  ? styles.mensagemConfirmacaoEnvioContainerLoading
                  : '')
              }
            >
              Deseja confirmar envio da declaração em nome de <strong>{declaranteData?.nomeRazaoSocial}</strong>?
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button secondary small m={2} onClick={() => setIsModalEnviarDeclaracaoOpen(false)}>
            Cancelar
          </Button>
          <CustomConfirmer
            key={'' + habilitarBotaoConfirmarEnvioOutrosRegimes + '' + habilitarBotaoConfirmarEnvioPLG}
            asyncCall={() => enviarDeclaracao()}
            data-testid='btn-confirmar-envio'
            disabled={
              (step === 3 && (!habilitarBotaoConfirmarEnvioOutrosRegimes || !habilitarBotaoConfirmarEnvioPLG)) ||
              isLoadingOutrosRegimesCheckBox ||
              isLoadingPLGCheckBox
            }
          >
            Confirmar
          </CustomConfirmer>
        </Modal.Footer>
      </Modal>

      <CustomDivider step={step} hasDadosDeclaracao />
      <TituloSection type='titular-ou-arrendatario' isSteps />

      {step === 0 && (
        <TitularArrendatarioPassoInicial
          declaracaoId={declaracaoId}
          declaranteData={declaranteData}
          isDeclaranteDataLoading={isDeclaranteDataLoading}
          regimeTributacao={regimeTributacao}
          setRegimeTributacao={setRegimeTributacao}
          setHasDeclaracaoInicial={setHasDeclaracaoInicial}
        />
      )}
      {step === 1 && <TitularArrendatarioDadosDeclaracao cpfCnpj={cpfCnpj} declaracaoId={declaracaoId} />}
      {step === 2 && (
        <TitularArrendatarioProcessoMinerario
          isNovaOperacaoPLG={isNovaOperacaoPLG}
          setNovaOperacaoPLG={setIsNovaOperacaoPLG}
          isEdicaoOutrosRegimes={isEdicaoOutrosRegimes}
          setIsEdicaoOutrosRegimes={setIsEdicaoOutrosRegimes}
          estadoTela={estadoTela}
          setEstadoTela={setEstadoTela}
        />
      )}
      {step === 3 && (
        <div ref={contentRef}>
          <TitularArrendatarioResumo
            abaSelecionada={abaSelecionada}
            setAbaSelecionada={setAbaSelecionada}
            periodoDeclaracao={
              mesParaAbreviacao(
                context?.declaracaoTitularArrendatario?.exercicioMes ||
                  declaracaoTitularArrendatarioInicial.exercicioMes
              ) +
              `/${context?.declaracaoTitularArrendatario?.exercicioAno || declaracaoTitularArrendatarioInicial.exercicioAno} `
            }
          />
        </div>
      )}
      <BotoesAcao
        isEdicaoOutrosRegimes={isEdicaoOutrosRegimes}
        isRetificadora={isRetificadora}
        declaracaoId={declaracaoId}
        estadoTela={estadoTela}
        step={step}
        setStep={setStep}
        isNovaOperacaoPLG={isNovaOperacaoPLG}
        setIsModalEnviarDeclaracaoOpen={setIsModalEnviarDeclaracaoOpen}
        isDeclaranteDataLoading={isDeclaranteDataLoading}
        isDeclaracaoDataLoading={isDeclaracaoDataLoading}
        regimeTributacao={regimeTributacao}
        hasDeclaracaoInicial={hasDeclaracaoInicial}
      />
    </>
  );
}

export default TitularArrendatarioEdit;
